import Jsona from "jsona";
import qs from "qs";
import axios from "axios";

const jsona = new Jsona();
const url = process.env.VUE_APP_API_BASE_URL;
const includes = [
  "organization",
  "items",
  "items.storable",
  "items.unitOfMeasureUnit",
  "sourceEstablishment",
  "sourceEstablishment.warehouse",
  "destinationEstablishment",
  "sourceCafeteria",
  "destinationCafeteria",
  "submittedBy",
  "acceptedBy",
  "rejectedBy",
  "createdBy",
];
const resource = "establishment-internal-orders";

function serializeParams(params) {
  return qs.stringify(params, { encode: false });
}

/**
 * These routes live inside the JsonApi::register group: its NegotiateContent
 * middleware rejects a request body that is not announced as JSON:API with a 415.
 */
const jsonApiOptions = {
  headers: {
    Accept: "application/vnd.api+json",
    "Content-Type": "application/vnd.api+json",
  },
};

export default {
  list({
    filter = {},
    include = [],
    sort = "-created_at",
    page = { number: 1, size: 25 },
  } = {}) {
    const params = {
      include: include.length ? include : includes.join(","),
      sort,
      page,
      filter,
    };

    const options = {
      params,
      paramsSerializer: serializeParams,
    };

    return axios.get(`${url}/${resource}`, options).then((response) => ({
      list: jsona.deserialize(response.data),
      meta: response.data.meta,
    }));
  },

  get({ id, include = [] }) {
    const params = {
      include: include.length ? include : includes.join(","),
    };
    const options = {
      params,
      paramsSerializer: serializeParams,
    };
    return axios
      .get(`${url}/${resource}/${id}`, options)
      .then((response) => jsona.deserialize(response.data));
  },

  add({ data, include = [] }) {
    const payload = jsona.serialize({
      stuff: data,
      includeNames: null,
    });

    const params = {
      include: include.length ? include : includes.join(","),
    };

    return axios
      .post(`${url}/${resource}?${serializeParams(params)}`, payload, {
        headers: {
          Accept: "application/vnd.api+json",
          "Content-Type": "application/vnd.api+json",
        },
      })
      .then((response) => jsona.deserialize(response.data));
  },

  update({ data, include = [] }) {
    const payload = jsona.serialize({
      stuff: data,
      includeNames: [],
    });

    const params = {
      include: include.length ? include : includes.join(","),
    };

    return axios
      .patch(`${url}/${resource}/${data.id}?${serializeParams(params)}`, payload, {
        headers: {
          Accept: "application/vnd.api+json",
          "Content-Type": "application/vnd.api+json",
        },
      })
      .then((response) => jsona.deserialize(response.data));
  },

  destroy({ id }) {
    return axios.delete(`${url}/${resource}/${id}`);
  },

  validate({ id, include = [] }) {
    const params = { include: include.length ? include : includes.join(",") };
    return axios
      .get(`${url}/${resource}/${id}/validate?${serializeParams(params)}`)
      .then((response) => jsona.deserialize(response.data));
  },

  accept({ id, include = [] }) {
    const params = { include: include.length ? include : includes.join(",") };
    return axios
      .get(`${url}/${resource}/${id}/accept?${serializeParams(params)}`)
      .then((response) => jsona.deserialize(response.data));
  },

  /**
   * Refuser la semaine. Le motif est obligatoire : le satellite doit apprendre
   * pourquoi rien ne sera produit, et il en est averti par courriel.
   */
  reject({ id, reasonCode, reason = null, include = [] }) {
    const params = { include: include.length ? include : includes.join(",") };
    return axios
      .post(
        `${url}/${resource}/${id}/reject?${serializeParams(params)}`,
        { reason_code: reasonCode, reason },
        jsonApiOptions
      )
      .then((response) => jsona.deserialize(response.data));
  },

  cancel({ id, include = [] }) {
    const params = { include: include.length ? include : includes.join(",") };
    return axios
      .get(`${url}/${resource}/${id}/cancel?${serializeParams(params)}`)
      .then((response) => jsona.deserialize(response.data));
  },

  revertToDraft({ id, include = [] }) {
    const params = { include: include.length ? include : includes.join(",") };
    return axios
      .get(`${url}/${resource}/${id}/revert-to-draft?${serializeParams(params)}`)
      .then((response) => jsona.deserialize(response.data));
  },

  /**
   * The receiving establishment turns one day of the order into an internal
   * invoice. `items` is an optional map of orderItemId -> { quantity } holding
   * the quantities actually received. When every quantity is 0 the day is
   * declared not received, and `reasonCode` / `reason` are required.
   */
  generateInternalInvoice({ id, deliveryDate, items = {}, reasonCode = null, reason = null }) {
    return axios
      .post(
        `${url}/${resource}/${id}/generate-internal-invoice`,
        { delivery_date: deliveryDate, items, reason_code: reasonCode, reason },
        jsonApiOptions
      )
      .then((response) => jsona.deserialize(response.data));
  },

  /**
   * E-mail the weekly order (PDF attached) to the production establishment.
   * Same payload shape as the sales order submission mail.
   */
  sendMail(data) {
    const payload = {
      ...data,
      id: undefined,
    };

    return axios
      .post(`${url}/${resource}/${data?.id}/send-mail`, payload, jsonApiOptions)
      .then((response) => response.data);
  },

  /**
   * PDF of the week, or the sending slip of one day when `date` is given.
   */
  print({ id, date = null }) {
    const options = {
      headers: {},
      responseType: "blob",
      params: date ? { date } : {},
    };
    return axios.get(`${url}/${resource}/${id}/print`, options);
  },

  /**
   * What the production kitchen has to make and send on a given day, across all
   * the satellites it serves.
   */
  dailyPreparations({ date, sourceEstablishmentId = null, destinationEstablishmentId = null }) {
    const params = {
      date,
      source_establishment_id: sourceEstablishmentId,
      destination_establishment_id: destinationEstablishmentId,
    };

    return axios
      .get(`${url}/${resource}/daily-preparations`, {
        params,
        paramsSerializer: serializeParams,
      })
      .then((response) => response.data);
  },

  /**
   * PDF of "Total par produit, toutes cafétérias satellites" for the day or the
   * week containing `date` (same figures as the screen).
   */
  printPreparations({ date, view = "day", sourceEstablishmentId }) {
    const kind = view === "week" ? "weekly" : "daily";
    return axios.get(`${url}/${resource}/${kind}-preparations/print`, {
      headers: {},
      responseType: "blob",
      params: { date, source_establishment_id: sourceEstablishmentId },
    });
  },

  /**
   * Same as dailyPreparations over the monday to friday week containing `date`,
   * with one quantity per day.
   */
  weeklyPreparations({ date, sourceEstablishmentId = null, destinationEstablishmentId = null }) {
    const params = {
      date,
      source_establishment_id: sourceEstablishmentId,
      destination_establishment_id: destinationEstablishmentId,
    };

    return axios
      .get(`${url}/${resource}/weekly-preparations`, {
        params,
        paramsSerializer: serializeParams,
      })
      .then((response) => response.data);
  },
};
